import React from "react";
import PropTypes from "prop-types";
import styles from "./LoadingScreenLayout.scss";
import { Column } from "../layout/Column";
import { AppLogo } from "../misc/AppLogo";
import classNames from "classnames";
import { useCssBreakpoints } from "react-use-css-breakpoints";
import { useMobile } from "./hooks";

export function LoadingScreenLayout({ center, bottom }) {
  const mobile = useMobile();
  return (
    <div className={classNames(styles.loadingScreenLayout, "tw-bg-[#252628]")}>
      <div
        className={classNames("tw-bg-[#ffffff]/50", "tw-rounded-2xl", {
          [classNames("tw-pt-4", "tw-px-6", "tw-pb-6")]: mobile,
          [classNames("tw-pt-8", "tw-px-16", "tw-pb-16")]: !mobile
        })}
      >
        <div className={classNames("tw-flex", "tw-flex-col")}>
          <div
            className={classNames("tw-font-bold", "tw-text-center", {
              ["tw-text-2xl"]: mobile,
              ["tw-text-3xl"]: !mobile
            })}
          >
            使い方
          </div>
          <div
            className={classNames("tw-grid", {
              [classNames(
                "landscape:tw-grid-cols-[160px_auto_160px]",
                "landscape:tw-grid-flow-col",
                "tw-gap-6",
                "tw-mt-6"
              )]: mobile,
              [classNames("tw-grid-cols-[288px_auto_288px]", "tw-grid-flow-col", "tw-gap-14", "tw-mt-10")]: !mobile
            })}
          >
            <div>
              {mobile ? (
                <div
                  className={classNames(
                    "tw-font-bold",
                    "tw-text-sm",
                    "tw-text-center",
                    "tw-grid",
                    "tw-whitespace-nowrap"
                  )}
                >
                  <span>画面左下の操作UIで</span>
                  <span>アバターの移動ができます</span>
                </div>
              ) : (
                <div className={classNames("tw-font-bold", "tw-text-2xl", "tw-text-center", "tw-grid")}>
                  <span>WASDキー or 矢印キーで</span>
                  <span>アバターの移動ができます</span>
                </div>
              )}
              <div
                className={classNames("tw-grid", "tw-place-content-center", {
                  ["tw-mt-4"]: mobile,
                  ["tw-mt-6"]: !mobile
                })}
              >
                {mobile ? (
                  <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M94 50C94 74.3005 74.3005 94 50 94C25.6995 94 6 74.3005 6 50C6 25.6995 25.6995 6 50 6C74.3005 6 94 25.6995 94 50Z"
                      fill="black"
                      fillOpacity="0.6"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M100 50C100 77.6142 77.6142 100 50 100C22.3858 100 0 77.6142 0 50C0 22.3858 22.3858 0 50 0C77.6142 0 100 22.3858 100 50ZM50 94C74.3005 94 94 74.3005 94 50C94 25.6995 74.3005 6 50 6C25.6995 6 6 25.6995 6 50C6 74.3005 25.6995 94 50 94Z"
                      fill="white"
                    />
                    <path
                      d="M66.2472 50C66.2472 58.9725 58.9735 66.2462 50.001 66.2462C41.0285 66.2462 33.7548 58.9725 33.7548 50C33.7548 41.0275 41.0285 33.7539 50.001 33.7539C58.9735 33.7539 66.2472 41.0275 66.2472 50Z"
                      fill="white"
                      fillOpacity="0.9"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M44.7265 24L50.5 14L56.2735 24H51.5V38C51.5 38.5523 51.0523 39 50.5 39C49.9477 39 49.5 38.5523 49.5 38V24H44.7265ZM24.5 55.7735L14.5 50L24.5 44.2265L24.5 49L38.5 49C39.0523 49 39.5 49.4477 39.5 50C39.5 50.5523 39.0523 51 38.5 51L24.5 51L24.5 55.7735ZM76.5 44.2265L86.5 50L76.5 55.7735V51H62.5C61.9477 51 61.5 50.5523 61.5 50C61.5 49.4477 61.9477 49 62.5 49H76.5V44.2265ZM50.5 61C51.0523 61 51.5 61.4477 51.5 62V76H56.2735L50.5 86L44.7265 76H49.5V62C49.5 61.4477 49.9477 61 50.5 61Z"
                      fill="black"
                    />
                  </svg>
                ) : (
                  <svg width="244" height="160" viewBox="0 0 244 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M94 10V66H150V10H94ZM91 0C87.134 0 84 3.13401 84 7V69C84 72.866 87.134 76 91 76H153C156.866 76 160 72.866 160 69V7C160 3.13401 156.866 0 153 0H91Z"
                      fill="black"
                    />
                    <path
                      d="M138.173 26.664L131.933 49H126.653L122.461 33.096L118.077 49L112.829 49.032L106.813 26.664H111.613L115.549 44.008L120.093 26.664H125.085L129.373 43.912L133.341 26.664H138.173Z"
                      fill="black"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M10 94V150H66V94H10ZM7 84C3.13401 84 0 87.134 0 91V153C0 156.866 3.13401 160 7 160H69C72.866 160 76 156.866 76 153V91C76 87.134 72.866 84 69 84H7Z"
                      fill="black"
                    />
                    <path
                      d="M42.9509 128.744H34.0549L32.5829 133H27.8789L35.9109 110.632H41.1269L49.1589 133H44.4229L42.9509 128.744ZM41.7349 125.16L38.5029 115.816L35.2709 125.16H41.7349Z"
                      fill="black"
                    />
                    <path
                      d="M122.17 133.224C120.613 133.224 119.205 132.957 117.946 132.424C116.709 131.891 115.727 131.123 115.002 130.12C114.277 129.117 113.903 127.933 113.882 126.568H118.682C118.746 127.485 119.066 128.211 119.642 128.744C120.239 129.277 121.05 129.544 122.074 129.544C123.119 129.544 123.941 129.299 124.538 128.808C125.135 128.296 125.434 127.635 125.434 126.824C125.434 126.163 125.231 125.619 124.826 125.192C124.421 124.765 123.909 124.435 123.29 124.2C122.693 123.944 121.861 123.667 120.794 123.368C119.343 122.941 118.159 122.525 117.242 122.12C116.346 121.693 115.567 121.064 114.906 120.232C114.266 119.379 113.946 118.248 113.946 116.84C113.946 115.517 114.277 114.365 114.938 113.384C115.599 112.403 116.527 111.656 117.722 111.144C118.917 110.611 120.282 110.344 121.818 110.344C124.122 110.344 125.989 110.909 127.418 112.04C128.869 113.149 129.669 114.707 129.818 116.712H124.89C124.847 115.944 124.517 115.315 123.898 114.824C123.301 114.312 122.501 114.056 121.498 114.056C120.623 114.056 119.919 114.28 119.386 114.728C118.874 115.176 118.618 115.827 118.618 116.68C118.618 117.277 118.81 117.779 119.194 118.184C119.599 118.568 120.09 118.888 120.666 119.144C121.263 119.379 122.095 119.656 123.162 119.976C124.613 120.403 125.797 120.829 126.714 121.256C127.631 121.683 128.421 122.323 129.082 123.176C129.743 124.029 130.074 125.149 130.074 126.536C130.074 127.731 129.765 128.84 129.146 129.864C128.527 130.888 127.621 131.709 126.426 132.328C125.231 132.925 123.813 133.224 122.17 133.224Z"
                      fill="black"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M94 94V150H150V94H94ZM91 84C87.134 84 84 87.134 84 91V153C84 156.866 87.134 160 91 160H153C156.866 160 160 156.866 160 153V91C160 87.134 156.866 84 153 84H91Z"
                      fill="black"
                    />
                    <path
                      d="M205.047 110.664C207.394 110.664 209.453 111.123 211.223 112.04C213.015 112.957 214.391 114.269 215.351 115.976C216.333 117.661 216.823 119.624 216.823 121.864C216.823 124.104 216.333 126.067 215.351 127.752C214.391 129.416 213.015 130.707 211.223 131.624C209.453 132.541 207.394 133 205.047 133H197.239V110.664H205.047ZM204.887 129.192C207.234 129.192 209.047 128.552 210.327 127.272C211.607 125.992 212.247 124.189 212.247 121.864C212.247 119.539 211.607 117.725 210.327 116.424C209.047 115.101 207.234 114.44 204.887 114.44H201.719V129.192H204.887Z"
                      fill="black"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M178 94V150H234V94H178ZM175 84C171.134 84 168 87.134 168 91V153C168 156.866 171.134 160 175 160H237C240.866 160 244 156.866 244 153V91C244 87.134 240.866 84 237 84H175Z"
                      fill="black"
                    />
                  </svg>
                )}
              </div>
            </div>
            <div className={classNames("tw-bg-[#000000]/20")}>
              <div className={classNames("tw-w-[2px]", "tw-h-[2px]")} />
            </div>
            <div>
              {mobile ? (
                <div
                  className={classNames(
                    "tw-font-bold",
                    "tw-text-sm",
                    "tw-text-center",
                    "tw-grid",
                    "tw-whitespace-nowrap"
                  )}
                >
                  <span>画面をスワイプすると</span>
                  <span>視点が移動できます</span>
                </div>
              ) : (
                <div className={classNames("tw-font-bold", "tw-text-2xl", "tw-text-center", "tw-grid")}>
                  <span>マウスをドラッグすると</span>
                  <span>視点が移動できます</span>
                </div>
              )}
              <div
                className={classNames("tw-grid", "tw-place-content-center", {
                  ["tw-mt-4"]: mobile,
                  ["tw-mt-6"]: !mobile
                })}
              >
                {mobile ? (
                  <>
                    <svg width="78" height="20" viewBox="0 0 78 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12.4855 10.4804L17.4162 15.4227L0 20L4.7248 2.70132L10.2092 8.19868C19.0481 3.07426 30.6785 0 39 0C48.2096 0 59.6138 2.741 68.1919 7.8689L73.8833 2.54809L78 20L60.7545 14.822L65.8213 10.0851C57.8758 5.56688 47.4447 3.10493 39 3.10493C31.4704 3.10493 20.7753 5.86185 12.4855 10.4804Z"
                        fill="black"
                      />
                    </svg>
                    <svg width="72" height="85" viewBox="0 0 72 85" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M16.0332 52.1388C13.4632 55.1386 12.8455 59.1291 14.1802 64.1101C16.1822 71.5817 28.7599 81.8995 37.0162 79.6872C45.2724 77.475 50.0606 76.192 58.1416 74.0267C66.2227 71.8614 69.2323 63.4602 67.709 57.7751C65.5638 49.7693 63.4187 41.7637 61.2736 33.7581C60.4566 30.709 57.3227 28.8997 54.2736 29.7167L54.2569 29.7211C51.2172 30.5356 49.4132 33.6602 50.2277 36.6999"
                        stroke="black"
                        strokeWidth="5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M19.7538 55.6052L9.67534 17.9918C8.85902 14.9453 10.6669 11.8139 13.7134 10.9975C13.7185 10.9962 13.7237 10.9948 13.7287 10.9935C16.7883 10.1827 19.9273 12.0007 20.7465 15.0581L28.4237 43.7098"
                        stroke="black"
                        strokeWidth="5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M28.1354 42.6352L26.1666 35.2875C25.3454 32.2228 27.1642 29.0726 30.2289 28.2514C33.2937 27.4302 36.4437 29.249 37.2649 32.3137L39.2337 39.6615"
                        stroke="black"
                        strokeWidth="5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M39.0472 39.1352L37.7097 34.1438C36.8928 31.0949 38.7021 27.9608 41.7512 27.1438C44.8003 26.3268 47.9342 28.1363 48.7512 31.1852L50.2305 36.706"
                        stroke="black"
                        strokeWidth="5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                ) : (
                  <>
                    <svg width="100" height="24" viewBox="0 0 100 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16.0909 12.5612L22.3684 18.5944L0 24L6.21021 3.06508L13.1931 9.77616C24.5115 3.66425 39.3654 0 50 0C61.7671 0 76.3308 3.26524 87.3148 9.37564L94.5634 2.87532L100 24L77.8438 17.8689L84.2959 12.0829C74.1199 6.69815 60.795 3.76471 50 3.76471C40.3707 3.76471 26.7076 7.05188 16.0909 12.5612Z"
                        fill="black"
                      />
                    </svg>
                    <svg width="100" height="160" viewBox="0 0 100 160" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M50 30C51.3261 30 52.5979 30.5268 53.5355 31.4645C54.4732 32.4021 55 33.6739 55 35V55C55 56.3261 54.4732 57.5979 53.5355 58.5355C52.5979 59.4732 51.3261 60 50 60C48.6739 60 47.4021 59.4732 46.4645 58.5355C45.5268 57.5979 45 56.3261 45 55V35C45 33.6739 45.5268 32.4021 46.4645 31.4645C47.4021 30.5268 48.6739 30 50 30ZM90 110C90 120.609 85.7857 130.783 78.2843 138.284C70.7828 145.786 60.6087 150 50 150C39.3913 150 29.2172 145.786 21.7157 138.284C14.2143 130.783 10 120.609 10 110V50C10 39.3913 14.2143 29.2172 21.7157 21.7157C29.2172 14.2143 39.3913 10 50 10C60.6087 10 70.7828 14.2143 78.2843 21.7157C85.7857 29.2172 90 39.3913 90 50V110ZM50 0C36.7392 0 24.0215 5.26784 14.6447 14.6447C5.26784 24.0215 0 36.7392 0 50V110C0 123.261 5.26784 135.979 14.6447 145.355C24.0215 154.732 36.7392 160 50 160C63.2608 160 75.9785 154.732 85.3553 145.355C94.7322 135.979 100 123.261 100 110V50C100 36.7392 94.7322 24.0215 85.3553 14.6447C75.9785 5.26784 63.2608 0 50 0Z"
                        fill="black"
                      />
                    </svg>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {bottom && <div className="tw-w-full">{bottom}</div>}
    </div>
  );
}

LoadingScreenLayout.propTypes = {
  center: PropTypes.node,
  bottom: PropTypes.node
};
